import uuid from "uuid/v4";
import { transform } from "objectro";
import isoCountries from "i18n-iso-countries";
import isoCountriesEn from "i18n-iso-countries/langs/en.json";
import {
  Place,
  LocalBusiness,
  Customer,
  CustomerRequest,
  CustomerResponse,
  CustomerResponseError,
  AppConfig,
  AzureCustomerRequest,
  CustomerSignatureResponse,
  AppServiceOidc,
  CustomerResponsePending
} from "../types";
import config, { getConfigValue } from "../config";
import debug from "./debug";
import {
  get,
  hasAndIsNotEmpty,
  stripNullProperties,
  getRetUrl
} from "./helpers";
import { getStoredItem, setStoredItem } from "./localStorage";
import queue from "./queue";
import queueStatus from "./queueStatus";
import { Notification } from "./NotificationsStore";
import appEvents from "./appEvents";
import { addNotification } from "./notifications";
import { getStoreById } from "./stores";
import { SFDC } from "../utils/sfdc";
import { CustomerFormStateObject } from "../hooks/useCustomerFormState";
import {
  CustomerDataStateObject,
  ExistingCustomerDataStateObject
} from "../hooks/useCustomerDataState";
import { ReactSelectOptionObject } from "../components/InputSelectCustom";
import { customerRequestToXml } from "../config/shared/transforms";
import { getIso3 } from "./countries";
import useFormState from "../hooks/useFormState";

isoCountries.registerLocale(isoCountriesEn);
export interface VerifySalesPersonIdResponse {
  salesPersonId: string;
  success: boolean;
  valid?: boolean;
  error?: string;
}
export interface addressDQEValidation {
  addressCountry?: string | null | undefined;
  addressLocality?: string | null | undefined;
  addressRegion?: string | null | undefined;
  streetAddress1?: string | null | undefined;
  streetAddress2?: string | null | undefined;
  postalCode?: string | null | undefined;
}
export interface SaveCustomerDataResponse {
  success: boolean;
  notification?: Notification;
}

export interface BackendResponse {
  status: string;
  statusCode?: number;
  data?: any;
  code?: number;
  message?: string;
}

export const TEST_KEYWORDS = [
  "ddc-test-save",
  "ddc-test-invalid",
  "ddc-test-reject",
  "ddc-test-crash"
];

/**
 * Check if the device is connected to the network (a.k.a. online)
 */
export function checkIsOnline(): boolean {
  return typeof navigator !== "undefined" &&
    typeof navigator.onLine === "boolean"
    ? navigator.onLine
    : true;
}

export function countryFromStore(store: LocalBusiness | undefined): string {
  return store ? store.geo.addressCountry : "UNKNOWN";
}

export function nameFromStore(store: LocalBusiness | undefined): string {
  return store ? store.name : "UNKNOWN";
}

/**
 * Validate the customer data to see if it is fit for sending.
 *
 * Throws errors, otherwise... _silence_...
 *
 * @NOTE this is actually maison-specific, so it's probably not
 * right to put this strong opinion here. For the most part we
 * can rely on the <ConfigurableForm /> validation schemas to
 * validate the data sent through.
 */
export function validateCustomerData(
  data: Partial<CustomerFormStateObject>
): void {
  [
    "honorificPrefix",
    "givenName",
    "familyName",
    "salesPerson",
    "creationStore",
    "signatureData"
  ].forEach(propName => {
    if (!get(data, propName)) {
      throw new Error(
        `Customer data does not contain required field "${propName}"`
      );
    }
  });
}

export function getStoredApiToken() {
  return getStoredItem("apiToken");
}

class BackendError extends Error {
  public code: number;

  constructor(message: string, code: number) {
    // Pass remaining arguments (including vendor specific ones) to parent constructor
    super(message);

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, BackendError);
    }
    this.name = "BackendError";
    this.code = code;
  }
}

export async function callBackend(
  endpoint: string,
  options: any,
  isAbsoluteUrl?: boolean
): Promise<any> {
  const baseUrl =
    getStoredItem("backendBaseUrl") || get(config, "services.api.baseUrl");
  const endpointUrl =
    endpoint.substr(0, 4) === "http" ? endpoint : baseUrl + endpoint;
  debug("[api] callBackend:", endpointUrl, options);
  const response: BackendResponse = await fetch(endpointUrl, options)
    .then((res: any) => {
      //debug("[api] callBackend: " + endpoint + " status:", res.status);
      if (res.status === 401) {
        throw new BackendError("Unauthorized", 401);
      }
      if (res.status === 403) {
        appEvents.trigger("app.forbidden");
        throw new BackendError("FORBIDDEN", 403);
      }
      const contentType = res.headers.get("Content-Type");
      return contentType && contentType.indexOf("json") >= 0 ? res.json() : "";
    })
    .catch(err => {
      debug("[api] callBackend: error", err);
      return { message: err.message || err, code: err.code, status: "error" };
    });
  debug("[api] callBackend: " + endpoint + " response:", response);
  if (
    response.statusCode === 401 ||
    (response.data && response.data.status === 401) ||
    get(response, "message.code") === 401
  ) {
    const message =
      response.message || get(response, "data.response.result.data[0].message");
    throw new BackendError(message, 401);
  }
  if (
    response.status === "fail" ||
    (response.code && response.code !== 200 && response.code !== 201)
  ) {
    const messages = get(response, "data.message");
    const message =
      get(response, "data.response.result.data.error.message") ||
      get(response, "data.response.result.data.errorCode") ||
      (Array.isArray(messages) && messages[0]) ||
      get(response, "data.message") ||
      get(response, "data.result.data.error_description") ||
      get(response, "data.result.data.clientMessage") ||
      response.message ||
      get(response, "data.result.err") ||
      get(response, "data.result.data.errorCode");
    const statusCode = response.code || get(response, "data.status");
    throw new BackendError(message, statusCode);
  }
  return response.data || response;
}

export async function callBackendSF(
  endpoint: string,
  method: string,
  data: any
): Promise<any> {
  debug("[api] callBackendSF:", endpoint, data);
  const response: BackendResponse = await SFDC.apex
    .post(endpoint, data)
    .then((res: any) => {
      //debug("[api] callBackend: " + endpoint + " status:", res.status);
      if (res.status === 401) {
        throw new BackendError("Unauthorized", 401);
      }
      if (res.status === 403) {
        appEvents.trigger("app.forbidden");
        throw new BackendError("FORBIDDEN", 403);
      }
      return res;
    });
  debug("[api] callBackend: " + endpoint + " response:", response);
  if (
    response.statusCode === 401 ||
    (response.data && response.data.status === 401)
  ) {
    const message =
      response.message || get(response, "data.response.result.data[0].message");
    throw new BackendError(message, 401);
  }
  if (
    response.status === "fail" ||
    (response.code && response.code !== 200 && response.code !== 201)
  ) {
    const messages = get(response, "data.message");
    const message =
      get(response, "data.response.result.data.error.message") ||
      get(response, "data.response.result.data.errorCode") ||
      (Array.isArray(messages) && messages[0]) ||
      get(response, "data.message") ||
      get(response, "data.result.data.error_description") ||
      get(response, "data.result.data.clientMessage") ||
      response.message ||
      get(response, "data.result.err") ||
      get(response, "data.result.data.errorCode");
    const statusCode = response.code || get(response, "data.status");
    throw new BackendError(message, statusCode);
  }
  return response.data || response;
}

export async function callBackendGET(
  endpoint: string,
  refreshed?: boolean
): Promise<any> {
  //debug("[api] callBackendGET:", endpoint);
  return new Promise((resolve, reject) => {
    const headers: any = {
      Authorization: "Bearer " + getStoredApiToken(),
      //"Access-Token": getStoredApiToken(),
      "Content-Type": "application/json"
    };
    if (get(config, "api.addXmaisonHeader")) {
      headers["X-Maison"] = config.id;
    }
    return callBackend(endpoint, {
      method: "GET",
      headers: headers
    })
      .then((res: any) => resolve(res.result))
      .catch(async error => {
        debug("[api] callBackendGET: error:", error.code, error.message);
        if (error.code === 401) {
          if (!refreshed) {
            await actions.refreshApiToken().then(apiToken => {
              debug("[api] callBackendGET: refreshed!", apiToken);
              // retry
              resolve(callBackendGET(endpoint, true));
            });
          } else {
            // logout
            debug("[api] callBackendGET: logout");
            //appEvents.trigger("app.showLogin");
          }
        } else {
          reject(error);
        }
      });
  });
}

async function getNewApiTokenOidc(apiCode: string) {
  debug("[api] getNewApiTokenOidc: code:", apiCode);
  const oidcConfig: AppServiceOidc = config.services.oidc;

  var body = new URLSearchParams();
  body.append("client_id", oidcConfig.clientId);
  body.append("scope", oidcConfig.scope);
  body.append("code", apiCode);
  body.append("redirect_uri", getRetUrl());
  body.append("grant_type", "authorization_code");
  body.append("code_verifier", oidcConfig.codeVerifier);
  body.append("response_type", "id_token token code");

  const endpoint =
    oidcConfig.getTokenEndpoint.substr(0, 4) === "http"
      ? oidcConfig.getTokenEndpoint
      : oidcConfig.baseUrl + oidcConfig.getTokenEndpoint;
  const requestOptions = {
    method: "POST",
    headers: {
      ...(oidcConfig.getTokenHeaders || {}),
      "Content-Type": "application/x-www-form-urlencoded"
    },
    body: body,
    redirect: "follow"
  };
  debug("[api] getNewApiTokenOidc", endpoint, requestOptions, body);
  return callBackend(endpoint, requestOptions, true);
}

async function refreshApiTokenOidc() {
  debug("[api] refreshApiTokenOidc");
  const oidcConfig: AppServiceOidc = config.services.oidc;

  var body = new URLSearchParams();
  body.append("client_id", oidcConfig.clientId);
  body.append("refresh_token", getStoredItem("refreshToken"));
  body.append("grant_type", "refresh_token");

  const endpoint = oidcConfig.baseUrl + oidcConfig.getTokenEndpoint;
  const requestOptions = {
    method: "POST",
    headers: {
      ...(oidcConfig.getTokenHeaders || {}),
      "Content-Type": "application/x-www-form-urlencoded"
    },
    body: body,
    redirect: "follow"
  };
  debug("[api] refreshApiTokenOidc", endpoint, requestOptions, body);
  return callBackend(endpoint, requestOptions, true);
}

function storeToken(tokenResponse: object) {
  setStoredItem("apiToken", get(tokenResponse, "access_token"));
  setStoredItem("refreshToken", get(tokenResponse, "refresh_token"));
  setStoredItem("idToken", get(tokenResponse, "id_token"));
  const expiresInSeconds = get(tokenResponse, "expires_in");
  const expireDate = expiresInSeconds
    ? new Date(new Date().getTime() + expiresInSeconds * 1000)
    : null;
  setStoredItem("apiTokenExpires", expireDate);
}

export const actions = {
  /*
   * Gets token for api calls
   */
  getNewApiToken: async (apiCode: string): Promise<string> => {
    debug("[api] getNewApiToken: code:", apiCode);
    const endpoint =
      get(config, "services.api.getTokenEndpoint") +
      "?maison=" +
      get(config, "id") +
      "&redirectUri=" +
      encodeURIComponent(getRetUrl()) +
      "&code=" +
      apiCode;
    const tokenResponse: Promise<any> = config.services.oidc
      ? await getNewApiTokenOidc(apiCode)
      : await callBackend(endpoint, {
          method: "GET",
          headers: {
            "Content-Type": "application/json"
          }
        });
    storeToken(tokenResponse);
    return get(tokenResponse, "access_token");
  },

  refreshApiToken: async (): Promise<string> => {
    debug("[api] refreshApiToken");
    const endpoint =
      get(config, "services.api.refreshTokenEndpoint") +
      "?maison=" +
      get(config, "id") +
      "&refresh_token=" +
      getStoredItem("refreshToken");
    const tokenResponse: Promise<any> = config.services.oidc
      ? await refreshApiTokenOidc()
      : await callBackend(endpoint, {
          method: "GET",
          headers: {
            "Content-Type": "application/json"
          }
        }).catch(error => {
          debug("[api] refreshApiToken: error:", error);
          return null;
        });
    debug("[api] refreshApiToken: tokenResponse:", tokenResponse);
    storeToken(tokenResponse);
    return get(tokenResponse, "access_token");
  },

  getStoredApiToken: async (): Promise<string> => {
    return getStoredItem("apiToken");
  },

  /*getUserLogin: async (apiToken: string): Promise<string> => {
    const oidcConfig: AppServiceOidc = config.services.oidc;
    const response = await fetch(oidcConfig.userInfoEndpoint, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + apiToken
      }
    }).then((res: any) => res.json());
    //console.log("userinfo:", response);
    setStoredItem("login", response.email);
    return response.email;
  },*/

  logout: (): void => {
    debug("[api] logout");
    appEvents.trigger("app.logout");
    storeToken({});
    if (config.services.sfdc) {
      appEvents.trigger("app.showLogin");
    } else if (!config.services.oidc) {
      const endpoint =
        get(config, "services.api.logoutEndpoint") +
        "?maison=" +
        get(config, "id") +
        "&refresh_token=" +
        getStoredItem("refreshToken");
      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        }
      };
      callBackend(endpoint, options)
        .then(() => {
          debug("[api] logout done");
        })
        .catch(error => {
          debug("[api] logout: error:", error);
          return null;
        });
    }
  },

  /**
   * Get the list of config from the API.
   */
  getConfig: async (): Promise<AppConfig> => {
    if (config.services.sfdc) {
      return SFDC.apex
        .get(config.services.api.getConfigEndpoint)
        .then((res: any) => res);
    }
    return callBackendGET(get(config, "services.api.getConfigEndpoint"))
      .then((res: any) => res || {})
      .catch((error: string) => {
        debug("[api] getConfig:", error);
        return {};
      });
  },

  /**
   * Get the list of titles from the API.
   */
  getTitles: async (): Promise<ReactSelectOptionObject[]> => {
    const handleError = (error: any) => {
      console.error("Unable to get titles data");
      appEvents.trigger("analytics.error", {
        category: "resources",
        action: "download_place_data",
        label: error,
        fatal: true
      });
      throw error;
    };
    const titlesRequest = config.services.sfdc
      ? SFDC.apex.get(config.api + "/titles")
      : callBackendGET(
          get(config, "services.api.getTitlesEndpoint", "/titles")
        );
    return titlesRequest
      .then((res: any) => {
        setStoredItem("titles", res);
        return res;
      })
      .catch((error: any) => {
        const storeTitles = getStoredItem("titles");
        if (storeTitles) {
          return storeTitles;
        }
        handleError(error);
      });
  },

  /**
   * Get the list of stores from the API.
   */
  getStores: async (): Promise<LocalBusiness[]> => {
    const handleError = (error: any) => {
      console.error("Unable to get store data");
      appEvents.trigger("analytics.error", {
        category: "resources",
        action: "download_place_data",
        label: error,
        fatal: true
      });
      throw error;
    };
    if (config.services.sfdc) {
      return SFDC.apex
        .get(config.api + "/stores")
        .then((res: any) => res)
        .catch((error: any) => {
          handleError(error);
        });
    }
    return callBackendGET(
      get(config, "services.api.getStoresEndpoint", "/stores")
    )
      .then((res: any) => res || [])
      .catch((error: any) => {
        handleError(error);
      });
  },

  validateMail: async (email: string): Promise<any> => {
    if (!email) {
      return {
        IdError: "00"
      };
    }

    email = email || "";

    // 2. DQE validation
    const endpoint = get(config, "services.api.dqeEmailEndpoint").replace(
      "{{email}}",
      email
    );

    return await fetch(endpoint, {
      method: "GET",
      redirect: "follow"
    })
      .then(response => response.json())
      .then(result => {
        if (result.data === "") {
          return {
            IdError: 0
          };
        }
        return { IdError: result[1].IdError };
      })
      .catch(error => {
        debug("[api] validateMail: received error from API", error);
        return {
          IdError: 0
        };
      });
  },

  /**
   * Get the list of places from the API.
   */
  getPlaces: async (): Promise<Place[]> => {
    const handleError = (error: any) => {
      console.error("Unable to get place data");
      appEvents.trigger("analytics.error", {
        category: "resources",
        action: "download_place_data",
        label: error,
        fatal: true
      });
      throw error;
    };

    if (config.services.sfdc) {
      return SFDC.apex
        .get(config.api + "/regions")
        .then((res: any) => res)
        .catch((error: any) => {
          handleError(error);
        });
    }
    return callBackendGET(
      get(config, "services.api.regionsEndpoint", "/places")
    )
      .then((res: any) => res)
      .catch((error: any) => {
        handleError(error);
      });
  },

  /**
   * Get the list of countries from the API.
   */
  getCountries: async (): Promise<Place[]> => {
    const handleError = (error: any) => {
      console.error("Unable to get country data");
      appEvents.trigger("analytics.error", {
        category: "resources",
        action: "download_country_data",
        label: error,
        fatal: true
      });
      throw error;
    };
    if (config.services.sfdc) {
      return SFDC.apex
        .get(config.api + "/countries")
        .then((res: any) => res)
        .catch((error: any) => {
          handleError(error);
        });
    }
    return callBackendGET(
      get(config, "services.api.getCountriesEndpoint", "/countries")
    )
      .then((res: any) => res)
      .catch((error: any) => {
        handleError(error);
      });
  },

  /**
   * Get the list of nationalities from the API.
   */
  getNationalities: async (): Promise<Place[]> => {
    const handleError = (error: any) => {
      console.error("Unable to get nationality data");
      appEvents.trigger("analytics.error", {
        category: "resources",
        action: "download_nationality_data",
        label: error,
        fatal: true
      });
      throw error;
    };
    if (config.services.sfdc) {
      return SFDC.apex
        .get(config.api + "/nationalities")
        .then((res: any) => res)
        .catch((error: any) => {
          handleError(error);
        });
    }
    return callBackendGET(
      get(config, "services.api.getNationalitiesEndpoint", "/nationalities")
    )
      .then((res: any) => res)
      .catch((error: any) => {
        handleError(error);
      });
  },

  /**
   * Verify a sales person ID.
   */
  verifySalesPersonId: async (
    salesPersonId: string
  ): Promise<VerifySalesPersonIdResponse> => {
    if (!salesPersonId) {
      throw new Error("No sales person ID given to validate");
    }

    const handleError = (error: any) => {
      debug("[api] verifySalesPersonId error:", error);
      debug("[api] verifySalesPersonId:", error.code, error.message);
      appEvents.trigger("analytics.error", {
        category: "sales_person",
        action: "verify_id",
        label: error,
        fatal: false
      });

      return {
        salesPersonId,
        success: false,
        error: error.message
      };
    };

    // Make the request to the API
    if (config.services.sfdc) {
      return (
        SFDC.apex
          .get(
            get(config, "services.api.verifyAdvisorEndpoint", "/person") +
              "/" +
              salesPersonId
          )
          .then(() => {
            return { salesPersonId, success: true, valid: true };
          })
          // Internal error or responses with status >= 500
          .catch((error: any) => {
            return handleError(error);
          })
      );
    }
    return (
      callBackendGET(
        get(config, "services.api.verifyAdvisorEndpoint", "/advisors") +
          "/" +
          salesPersonId
      )
        .then((res: any) => {
          debug("[api] verifySalesPersonId success:", res);
          return { salesPersonId, success: true, valid: true };
        })
        // Internal error or responses with status >= 500
        .catch((error: BackendError) => {
          return handleError(error);
        })
    );
  },

  getCustomerIdByHashcode: async (hashcode: string): Promise<string> => {
    const endpoint =
      get(config, "services.api.customerStatusEndpoint", "/clients?hashcode=") +
      hashcode;
    return callBackendGET(endpoint).then((data: any) => {
      //debug("[api] getCustomerByHashcode success:", data);
      const identifier = data.cegidId || data.salesforceId || data.identifier;
      return identifier;
    });
  },

  /**
   * Get a specific customer from the API.
   */
  getCustomerById: async (
    customerId: string
  ): Promise<CustomerDataStateObject> => {
    // Kind of important. I put this here just in case in production when TypeScript can't enforce.
    if (!customerId) {
      throw new Error("Missing customer ID");
    }

    // Get the required info to decorate the request
    // const traceToken = uuid();
    // const salesPersonId = getStoredItem("salesPersonId");
    // const storeId = getStoredItem("storeId");
    // const storeCountry: string = countryFromStore(await getStoreById(storeId));

    return callBackendGET(`/customer/${String(customerId)}`)
      .then((res: any) => {
        const customerData = transform(res, config.transforms.customer.read);
        debug("Received response from API", { res, customerData });
        return customerData;
      })
      .catch((err: any) => {
        debug("Received error from API", { err });
        throw err;
      });
  },

  searchCustomers: async (
    searchWord: string,
    appState?: any
  ): Promise<ExistingCustomerDataStateObject[]> => {
    if (!searchWord) {
      throw new Error("Missing search term");
    }

    const endpoint =
      get(config, "services.api.searchCustomersEndpoint") +
      encodeURIComponent(encodeURIComponent(searchWord)); // encode twice for the proxy
    if (config.services.sfdc) {
      return (
        SFDC.apex
          .get(endpoint)
          .then((res: any) => {
            //debug("[api] searchCustomers:", res);
            return res.map((customer: any) =>
              transform(
                customer,
                getConfigValue(appState.zone, "transforms.customer.read")
              )
            );
          })
          // Internal error or responses with status >= 500
          .catch((error: any) => {
            debug("[api] searchCustomers:", error);
          })
      );
    }
    return callBackendGET(endpoint)
      .then((res: any) => {
        debug("[api] searchCustomers: result", res);
        const transformObj = getConfigValue(
          appState.zone,
          "transforms.customer.read"
        );
        return (
          res && res.map((customer: any) => transform(customer, transformObj))
        );
      })
      .catch((err: any) => {
        debug("[api] searchCustomers: error: ", err);
        throw get(err, "data.error.message") || err;
      });
  },

  getCustomersHistory: async (): Promise<ExistingCustomerDataStateObject[]> => {
    const endpoint =
      get(config, "services.api.getCustomersHistoryEndpoint") +
      "?store=" +
      getStoredItem("storeId") +
      "&days=1";
    return callBackendGET(endpoint)
      .then((res: any) => {
        debug("[api] getCustomersHistory:", res);
        return (
          res &&
          res.map((customer: any) =>
            transform(customer, config.transforms.customer.read)
          )
        );
      })
      .catch((err: any) => {
        debug("Received error from API", { err });
        throw err;
      });
  },
  async validateDqeAddress(data: Partial<CustomerFormStateObject>) {
    const {
      addressCountry,
      addressLocality,
      addressRegion,
      streetAddress1,
      streetAddress2,
      postalCode
    } = data;

    const dqeValidationUrl = actions.buildUrl({
      addressCountry,
      addressLocality,
      addressRegion,
      streetAddress1,
      streetAddress2,
      postalCode
    });

    const response = await fetch(dqeValidationUrl).catch(e => {
      throw new Error("something went wrong!!!");
    });
    const jsonData = await response.json();
    const validCodes = ["10", "20", "21", "22", "23", "24", "25"];
    if (jsonData && jsonData["1"].DQECodeDetail) {
      const isValid = validCodes.includes(jsonData["1"].DQECodeDetail);
      return isValid;
    }
  },

  buildUrl(dqeObj: addressDQEValidation) {
    const addressUrlSturcture: string = `streetAddress2|streetAddress1|addressRegion|postalCode|addressLocality`;
    const urlAddressPart = addressUrlSturcture
      .split("|")
      .map((item: string) => {
        let _url = "";
        const value = dqeObj[item as keyof addressDQEValidation]
          ? dqeObj[item as keyof addressDQEValidation]
          : "";
        return (_url += `${value}|`);
      })
      .join("")
      .slice(0, -1);

    let countryCode;
    if (dqeObj.addressCountry) {
      countryCode = getIso3(dqeObj.addressCountry as string);
    }

    const url = `https://preprod.dqe-software.com/RNVP/?Adresse=${urlAddressPart}&Pays=${countryCode}&Licence=Y55-n2AP3_CwibTpB397WH1vGa`;
    return url;
  },

  checkIfToValidteAddress(data: Partial<CustomerFormStateObject>) {
    if (data && data.addressCountry) {
      const { streetAddress1 } = data;
      if (streetAddress1) return true;
    }
    return false;
  },

  /**
   * Save customer data to the API.
   */
  saveCustomerData: async (
    data: Partial<CustomerFormStateObject>
  ): Promise<SaveCustomerDataResponse> => {
    // Ensure customer data is valid first
    // validateCustomerData(data);

    const salesPersonId = String(
      data.salesPerson || getStoredItem("salesPersonId")
    );
    const storeId = String(data.creationStore || getStoredItem("storeId"));
    const store = await getStoreById(storeId);
    data.storeCountry = countryFromStore(store);
    data.storeName = nameFromStore(store);
    data.storeLocation = getStoredItem("zone");
    const countryName = isoCountries.getName(String(data.storeCountry), "en");

    // add birthday parts
    if (data.birthDate) {
      let birthdayParts = data.birthDate.split("-");
      data.birthDateYear = birthdayParts[0];
      data.birthDateMonth = birthdayParts[1];
      data.birthDateDay = birthdayParts[2];
    }

    // Transform the data to the shape that the DDC API expects
    // if (actions.checkIfToValidteAddress(data)) {
    //   const isAddressValid = await actions.validateDqeAddress(data);
    //   data.isAddressValid = isAddressValid;
    // }

    const customerData: Customer = stripNullProperties<Customer>(
      transform(data, config.transforms.customer.write),
      { returnNullIfEmpty: false }
    ) as Customer;

    // handle custom fields
    const customFields: string[] = get(config, "customise.fields.customFields");
    if (customFields) {
      if (!customerData.customFields) {
        customerData.customFields = [];
      }
      customFields.forEach(customField => {
        if (typeof data[customField] !== "undefined") {
          customerData.customFields.push({
            name: customField,
            value: data[customField]
          });
        }
      });
    }

    // convert language code
    // debugger;
    const backendCodes = get(config, "languages.backendCodes");
    const langCode = customerData.preferredLanguage;
    if (langCode && backendCodes && backendCodes[langCode]) {
      customerData.preferredLanguage = backendCodes[langCode];
    }

    // encode signature to base64
    if (
      customerData.signatureData &&
      get(config, "services.api.encodeSignature")
    ) {
      customerData.signatureData = btoa(customerData.signatureData);
    }
    // Prepare the customer request. This object will be used for any retries
    const customerReq: CustomerRequest = {
      requestId: uuid(),
      type: hasAndIsNotEmpty(data, "identifier") ? "update" : "create",
      traceToken: uuid(),
      salesPersonId,
      storeId,
      storeCountry: data.storeCountry,
      storeName: data.storeName,
      countryName,
      data: customerData,
      attempt: 1,
      isRemote: data.isRemote
    };

    debug("[api] saveCustomerData", {
      data,
      preparedData: customerData,
      customerReq
    });

    if (get(config, "services.api.signatureEndpoint")) {
      const signatureId = await actions.commitSignature(
        customerData.signatureData
      );
      if (signatureId) {
        customerData.id = signatureId;
        delete customerData.signatureData;
      }
    }
    return actions
      .commitCustomerData(customerReq)
      .then(
        async (customerRes: CustomerResponse): Promise<CustomerResponse> => {
          debug("[api] saveCustomerData: customerRes:", customerRes);

          if (
            // Disable the queue when running tests
            process.env.NODE_ENV !== "test" &&
            // Only add api_error to the queue as failure is fatal
            // which shouldn't be retried.
            get(customerRes, "errorType") === "api_error"
          ) {
            if (get(customerRes, "errorMessage") !== "Unauthorized") {
              debug("[api] saveCustomerData: add to queue");
              setTimeout(queue.addItem(customerReq), 4000);
            } else {
              const customerRetryRes: CustomerResponse = await actions.commitCustomerData(
                customerReq
              );
              debug("[api] saveCustomerData: customerRetryRes:", customerRes);

              if (get(customerRetryRes, "errorType") === "api_error") {
                debug("[api] saveCustomerData: add to queue");
                setTimeout(queue.addItem(customerReq), 4000);
              }
              return customerRetryRes;
            }
          }

          return customerRes;
        }
      )
      .then(async (customerRes: CustomerResponse) => {
        const notification = addNotification(customerReq, customerRes);

        return {
          success: hasAndIsNotEmpty(customerRes, "data"),
          notification,
          identifier: get(customerRes, "data.identifier"),
          barcode: get(customerRes, "data.barcode")
        };
      });
  },

  commitSignature: async (signatureData: string): Promise<string> => {
    let signatureRequest: AzureCustomerRequest = {
      fileName: "DigitalCardSignature.xml",
      content: "data:image/svg+xml;base64," + btoa(signatureData)
    };

    const requestOptions: any = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Ocp-Apim-Subscription-Key":
          get(config, "services.api.signatureKey") ||
          get(config, "services.api.key")
      },
      body: JSON.stringify(signatureRequest)
    };
    debug("[api] commitSignature: requestOptions:", requestOptions);

    let sigResponse: CustomerSignatureResponse = await fetch(
      get(config, "services.api.signatureEndpoint"),
      requestOptions
    )
      .then(response => {
        debug("[api] commitSignature: response:", JSON.stringify(response));
        if (response.status !== 200) {
          throw new Error(response.status + "");
        }
        return response.body ? response.json() : null;
      })
      .catch((error: any) => {
        debug("[api] commitSignature: received error from API", error);
        if (get(error, "response")) {
          debug("[api] commitSignature:", get(error, "response"));
        }
      });
    debug("[api] commitSignature: sigResponse:", sigResponse);

    if (sigResponse) {
      let name: string = get(sigResponse, "assets[0].name");
      // "name": "0_34.xml" => "34"
      let i = name.indexOf("_") + 1;
      return name.substr(i, name.indexOf(".xml") - i);
    } else {
      appEvents.trigger("analytics.error", {
        category: "customer_data",
        action: "committing_customer_data",
        label: `Cannot save signature`,
        fatal: true,
        dimensions: {
          ///...sharedInfo
        }
      });
      return "";
    }
  },

  /**
   * Commit customer data to the API.
   *
   * When customer requests are retried, they skip `the `saveCustomerData`
   * step and go straight here, since the `CustomerRequest` object has
   * already been made.
   */
  commitCustomerData: async (
    customerReq: CustomerRequest
  ): Promise<CustomerResponse> => {
    debug("[api] commitCustomerData", customerReq);

    // This info is shared between CustomerRequest objects and analytics events
    const sharedInfo = {
      traceToken: customerReq.traceToken, // dimension1
      salesPersonId: customerReq.salesPersonId, // dimension2
      storeId: customerReq.storeId, // dimension3
      storeCountry: customerReq.storeCountry, // dimension4
      attempt: customerReq.attempt, // dimension5
      requestId: customerReq.requestId, // dimension6
      storeName: customerReq.storeName, // dimension7
      countryName: customerReq.countryName // dimension8
    };

    if (get(config, "services.api.sendCustomerAsXml")) {
      return actions.commitCustomerDataAzure(customerReq.data, sharedInfo);
    }

    const endpoint =
      get(config, "services.api.customerEndpoint") +
      (customerReq.isRemote ? "?remote=true" : "");
    const headers: any = {
      Authorization: "Bearer " + getStoredApiToken(),
      "Content-Type": "application/json"
    };
    if (get(config, "api.addXmaisonHeader")) {
      headers["X-Maison"] = config.id;
    }
    const requestOptions: any = {
      method: "POST",
      headers: headers,
      body: JSON.stringify(customerReq.data)
    };
    const subscrKey = get(config, "services.api.key");
    if (subscrKey) {
      requestOptions.headers["Ocp-Apim-Subscription-Key"] = subscrKey;
    }
    //debug("[api] commitCustomerData: requestOptions:", requestOptions);

    let res: CustomerResponse = await (config.services.sfdc &&
    !endpoint.startsWith("http")
      ? callBackendSF(
          get(config, "services.api.customerEndpoint"),
          "post",
          customerReq.data
        )
      : callBackend(endpoint, requestOptions)
    )
      .then(response => {
        debug("[api] commitCustomerData: success:", response);
        if (config.transforms.customer.response) {
          response = transform(
            response.result || response,
            getConfigValue(
              customerReq.storeCountry.toLowerCase(),
              "transforms.customer.response"
            )
          );
        }
        //debug("[api] commitCustomerData: tRes:", res);
        if (response.status === "error") {
          throw new BackendError(response.message, response.status);
        }
        // handle duplicate response (e.g. sf and cegid)
        if (Array.isArray(response)) {
          response = response[1].data;
        }
        return response.result || response;
      })
      .catch(async (error: BackendError) => {
        debug("[api] commitCustomerData: error:", error);
        if (error.code === 401) {
          await actions.refreshApiToken().then(apiToken => {
            //debug("[api] commitCustomerData: refreshed:", apiToken);
            if (!apiToken) {
              appEvents.trigger("app.logout");
            }
          });
          // retry later in queue
        }
        const customerRes = actions.getCustomerResponseError(error, sharedInfo);
        actions.fireGoogleAnalyticsCustomerErrorEvent(customerRes, sharedInfo);
        return customerRes;
      });
    debug("[api] commitCustomerData: res:", res);

    // handle error response
    if (get(res, "errorCode")) {
      return res;
    }

    const identifier = get(res, "id");
    const type = get(res, "type");
    const barcode = get(res, "barcode");
    const hashcode = get(res, "hashcode");

    // Error dressed as success
    if (!identifier && !hashcode) {
      const noIdResponse: CustomerResponse = {
        ...sharedInfo,
        translationId: "notifications.messages.customerSaved.errors.server",
        errorType: "failure",
        errorCode: config.errorCodes.SERVER,
        errorMessage: "Server did not return client identifier"
      };
      return noIdResponse;
    }

    if (identifier) {
      actions.fireGoogleAnalyticsSuccessEvent(sharedInfo);
      const customerRes: CustomerResponse = {
        ...sharedInfo,
        data: {
          ...customerReq.data,
          identifier: identifier,
          type: type,
          barcode: barcode
        }
      };
      return customerRes;
    } else {
      // handle hashcode
      debug("[api] commitCustomerData: add to queueStatus:", hashcode);
      const customerRes: CustomerResponsePending = {
        ...sharedInfo,
        data: customerReq.data,
        hashcode: hashcode,
        requestDate: new Date()
      };
      setTimeout(() => {
        queueStatus.addItem(customerRes);
      }, 6000);
      return customerRes;
    }
  },

  commitCustomerDataAzure: async (
    customerData: Customer,
    sharedInfo: any
  ): Promise<CustomerResponse> => {
    debug("[api] commitCustomerDataAzure", customerData);

    let customerXmlStr = customerRequestToXml(customerData);
    debug("[api] commitCustomerDataAzure: customerXmlStr:", customerXmlStr);

    // encode for URI first to support non-latin characters
    let customerXmlStrUri = encodeURIComponent(customerXmlStr);

    let customerRequestAzure: AzureCustomerRequest = {
      fileName: "DigitalCardCustomer.xml",
      content: "data:image/svg+xml;base64," + btoa(customerXmlStrUri)
    };

    let requestOptions: any = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Ocp-Apim-Subscription-Key": get(config, "services.api.key")
      },
      body: JSON.stringify(customerRequestAzure || customerData)
    };
    debug("[api] commitCustomerDataAzure: requestOptions:", requestOptions);

    let res: CustomerResponse = await fetch(
      get(config, "services.api.customerEndpoint"),
      requestOptions
    )
      .then(response => {
        debug(
          "[api] commitCustomerDataAzure: response:",
          JSON.stringify(response)
        );
        if (response.status !== 200) {
          throw new Error(response.status + "");
        }
        return response.body ? response.json() : null;
      })
      .catch((err: any) => {
        const customerRes = actions.getCustomerResponseError(err, sharedInfo);
        actions.fireGoogleAnalyticsCustomerErrorEvent(customerRes, sharedInfo);
        return customerRes;
      });
    // res example: {"brand":"30","market":"FR","docType":"cylandeProspect","assets":[{"name":"0_5.xml","url":"https://retail-mediation-qa.azure-api.net/documents/v1/30/FR/cylandeProspect/0/assets/0_5.xml"}]}
    debug("[api] commitCustomerDataAzure: res:", res);
    let assetName = get(res, "assets[0].name");

    // Error dressed as success
    if (!res || !assetName) {
      const customerRes: CustomerResponse = {
        ...sharedInfo,
        translationId: "notifications.messages.customerSaved.errors.server",
        errorType: "api_error",
        errorCode: config.errorCodes.SERVER,
        errorMessage: "Server did not return client identifier"
      };
      return customerRes;
    }

    // if signature id is represented - use it instead of customer asset id
    let assetNumber = customerData.signatureId;
    if (!assetNumber) {
      // parse asset number
      let i = assetName.indexOf("_") + 1; // "name": "0_34.xml" => "34"
      assetNumber = assetName.substr(i, assetName.indexOf(".xml") - i);
    }

    const customerRes: CustomerResponse = {
      ...sharedInfo,
      data: { ...customerData, identifier: assetNumber }
    };

    actions.fireGoogleAnalyticsSuccessEvent(sharedInfo);

    return customerRes;
  },

  getCustomerResponseError(err: any, sharedInfo: any) {
    const customerRes: CustomerResponseError = {
      ...sharedInfo
    };

    // Network error, should allow retrying
    if (get(err, "response") === undefined && !checkIsOnline()) {
      customerRes.translationId =
        "notifications.messages.customerSaved.errors.network.body";
      customerRes.errorType = "api_error";
      customerRes.errorCode = config.errorCodes.NETWORK;
      customerRes.errorMessage = err.message;
    } else {
      switch (err.code) {
        // ValidationError from DDC API, should prevent retrying
        case 400:
        // Maison data source rejected customer data, should prevent retrying
        case 403:
          customerRes.translationId =
            "notifications.messages.customerSaved.errors.data";
          customerRes.errorType = "failure";
          customerRes.errorCode = config.errorCodes.DATA_ERROR;
          customerRes.errorMessage = err.message;
          break;

        // Critical server error (i.e. crash), should prevent retrying
        case 500:
          customerRes.translationId =
            "notifications.messages.customerSaved.errors.server";
          customerRes.errorType = "failure";
          customerRes.errorCode = config.errorCodes.SERVER;
          customerRes.errorMessage = err.message;
          break;

        // Data source experienced an error, should prevent retrying
        // @NOTE this has been changed to retry, just in case there
        // was a connection error
        case 502:
          customerRes.translationId =
            "notifications.messages.customerSaved.errors.dataSource";
          customerRes.errorType = "api_error";
          customerRes.errorCode = config.errorCodes.DATA_CONNECTION;
          customerRes.errorMessage = err.message;
          break;

        // Default to server error (i.e. timeouts), should allow retrying
        default:
          customerRes.translationId =
            "notifications.messages.customerSaved.errors.server";
          customerRes.errorType = "api_error";
          customerRes.errorCode = config.errorCodes.SERVER;
          customerRes.errorMessage = err.message;
          break;
      }
    }
    return customerRes;
  },

  getPlacesDqe: async (country: string, value: string): Promise<any> => {
    //debug("[api] getPlacesDqe:", value);
    let iso3 = country;
    if (country.length === 2) {
      iso3 = getIso3(country) || "";
    }
    const endpoint = get(config, "services.api.dqeAddressesEndpoint")
      .replace("{{country}}", iso3)
      .replace("{{search}}", value);
    //debug("[api] getPlacesDqe:", endpoint);
    let response: any = await fetch(endpoint)
      .then(response => {
        //debug("[api] getPlacesDqe: response:", JSON.stringify(response));
        if (response.status !== 200) {
          throw new Error(response.status + "");
        }
        return response.body ? response.json() : null;
      })
      .catch((error: any) => {
        debug("[api] getPlacesDqe: received error from API", error);
        if (get(error, "response")) {
          debug("[api] getPlacesDqe:", get(error, "response"));
        }
      });
    //debug("[api] getPlacesDqe: response:", response);
    return response ? Object.values(response) : [];
  },

  fireAnalyticsError(
    category: string,
    action: string,
    error: any,
    fatal: boolean
  ) {
    appEvents.trigger("analytics.error", {
      category: category,
      action: action,
      label: error,
      fatal: fatal
    });
  },

  fireGoogleAnalyticsCustomerErrorEvent(
    customerRes: CustomerResponseError,
    sharedInfo: any
  ) {
    appEvents.trigger("analytics.error", {
      category: "customer_data",
      action: "committing_customer_data",
      label: `${customerRes.errorCode} - ${customerRes.errorMessage}`,
      fatal: customerRes.errorType === "failure",
      dimensions: {
        ...sharedInfo
      }
    });
  },

  fireGoogleAnalyticsSuccessEvent(sharedInfo: any) {
    appEvents.trigger("analytics.event", {
      category: "customer_data",
      action: "committed_customer_data",
      label: "Successfully saved customer data",
      dimensions: {
        ...sharedInfo
      }
    });
  }
};

export default actions;
